import { Alert, List, ListItem, Skeleton } from "@mui/material";
import * as React from "react";

import { VisibilityToggle } from "@/shared/components/VisibilityToggle";
import { EventRecord } from "@/shared/domain/events";
import { actions, useVizDispatch } from "@/shared/state/visualization";

import { useEventVisibilityResolver } from "../../eventVisibility";
import { SidebarViewType, useWorkspaceSidebar } from "../../sidebarContext";
import { EMPTY_KEYWORD_FILTER, type KeywordFilter } from "../../Topics/filter";
import { useWorkspaceEvents } from "../../WorkspaceCtx/useWorkspaceEvents";
import { FilterInput } from "../FilterInput";
import { makeMatcher } from "../FilterInput/makeMatcher";
import { FilterMatch } from "../filterResult";

import { Event } from "./Event";
import styles from "./EventList.module.css";

interface EventFilterResult {
  event: EventRecord;
  match?: FilterMatch;
}

export function EventList() {
  const eventsQuery = useWorkspaceEvents();
  const dispatch = useVizDispatch();

  const [filter, setFilter] =
    React.useState<KeywordFilter>(EMPTY_KEYWORD_FILTER);
  const isEventVisible = useEventVisibilityResolver();

  const sidebar = useWorkspaceSidebar();

  const matcher = React.useMemo(() => makeMatcher(filter), [filter]);

  // Compute filtered events with optional match metadata for highlighting.
  const filteredEvents = React.useMemo<EventFilterResult[]>(() => {
    if (eventsQuery.data === undefined) {
      return [];
    }

    const results: EventFilterResult[] = [];

    for (const event of eventsQuery.data) {
      const result = matcher(event.name);
      if (!result.matches) {
        continue;
      }

      const match: FilterMatch | undefined = result.position
        ? { position: result.position }
        : undefined;
      results.push({ event, match });
    }

    return results;
  }, [eventsQuery.data, matcher]);

  // The toggle mirrors the listed rows' *resolved* visibility (overrides and
  // the workspace rule included), so it reads "off" in a workspace whose rule
  // hides everything. An empty list reads "on", matching the empty state's
  // meaning of nothing-is-hidden.
  const allEventsVisible = filteredEvents.every((result) =>
    isEventVisible(result.event),
  );

  return (
    <div className={styles.eventListContainer}>
      <div className={styles.eventListControls}>
        <FilterInput
          className={styles.filterInput}
          placeholder="Filter events by name"
          onFilterChange={setFilter}
        />

        <VisibilityToggle
          on={allEventsVisible}
          onToggle={(on) => {
            dispatch(
              actions.setEventsVisibility(
                on,
                filteredEvents.map((result) => result.event),
              ),
            );
          }}
          className={styles.visibilityButton}
        />
      </div>

      <div className={styles.eventListContent}>
        <List dense disablePadding>
          {filteredEvents.map((result) => {
            const { event, match } = result;
            return (
              <Event
                key={event.event_id}
                event={event}
                match={match}
                onClick={function openEventDetails() {
                  sidebar.openView({
                    type: SidebarViewType.EventDetails,
                    data: {
                      eventId: event.event_id,
                    },
                  });
                }}
              />
            );
          })}

          {eventsQuery.isPending && (
            <>
              {Array.from({ length: 12 }).map((_, index) => (
                <ListItem key={index}>
                  <Skeleton width="100%" height="30px" variant="rounded" />
                </ListItem>
              ))}
            </>
          )}
        </List>

        {eventsQuery.data !== undefined && filteredEvents.length === 0 && (
          <Alert severity="info" className={styles.eventListAlert}>
            No matching events to show
          </Alert>
        )}
      </div>
    </div>
  );
}
